import React from 'react';
import {useNavigate} from "react-router-dom";
import {useQuery} from "@apollo/client";
import {AssetNameplateBasicInfo} from "../../graphql/queries/assetNameplateBasicInfo";
import {AssetIotConnection} from "../../graphql/queries/AssetIotConnection";
import {AssetDetail} from "../../graphql/queries/asset";
import {AssetBreadcrumbPath} from "../../graphql/queries/assetBreadcrumbPath";
import {getPathNodesAbove} from "../../utils/installedBasePath";
import InstalledBasePathLine from "../InstalledBasePathLine";
import _ from "lodash";
import {Chip, Typography} from "@mui/joy";
import {useTranslation} from "react-i18next";
import {Box, Button, Skeleton, Tooltip} from "@mui/material";
import AssetProductImage from "../AssetProductImage";
import NodeImageActionButton, {NODE_IMAGE_HOVER_TARGET_CLASS} from "../NodeImageActionButton";
import {getNodeImageThumbnail} from "../../utils/nodeDisplayImage";
import {buildNodeImageTarget} from "../../hooks/useNodeImage";
import {SxProps} from "@mui/system";
import {EngineeringRounded, OpenInNewOutlined, RemoveRounded} from "@mui-symbols-material/w300";
import {
    getIotStatusChipColors,
    getIotStatusLabel,
    IotStatusChipColors,
    IotStatusLabelOptions,
} from "../../config/iotConnectionStatusConfig";

// Both delegate to src/config/iotConnectionStatusConfig — the single source for
// IoT status labels and colors. Kept as named exports because several nameplate
// and hero components render the same chip; system chips pass
// `{ isSystem: true }` so entity-specific wording reads correctly.
export const getStatusChipColors = (connectionStatus: string): IotStatusChipColors =>
    getIotStatusChipColors(connectionStatus);

export const getStatusChipLabel = (
    t: (key: string) => string,
    connectionStatus: string,
    options?: IotStatusLabelOptions,
): string => getIotStatusLabel(connectionStatus, t, options);

interface KeyValueProps {
    title: string,
    value?: string | React.ReactNode,
    keySx?: SxProps,
    valueSx?: SxProps,
    truncateKey?: boolean,
}

export const KeyValueInformation = ({title, value, keySx, valueSx, truncateKey}: KeyValueProps) => {
    const keyTypography = truncateKey ? (
        <Typography
            level="body-sm"
            sx={{
                width: "40%",
                maxWidth: "160px",
                display: 'flex',
                whiteSpace: 'nowrap',
                ...keySx,
            }}
            color="neutral"
        >
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                {title}
            </span>
            <span style={{ flexShrink: 0 }}>:</span>
        </Typography>
    ) : (
        <Typography level="body-sm" sx={{ width: "40%", maxWidth: "160px", ...keySx }} color="neutral">
            {title}:
        </Typography>
    );

    return <Box display="flex" width="100%">
        {truncateKey ? (
            <Tooltip title={title} enterDelay={700} placement="top-start">
                {keyTypography}
            </Tooltip>
        ) : keyTypography}
        {
            !value || typeof value === "string" ?
                <Typography level="body-sm" fontWeight="lg" sx={{ width: "60%", wordBreak: "break-word", color: "#333", ...valueSx }}>
                    {value || "—"}
                </Typography>
            :
                value
        }
    </Box>
}

const SkeletonKeyValue = ({ title }: { title: string }) => (
    <Box display="flex" width="100%">
        <Typography level="body-sm" sx={{ width: "40%", maxWidth: "160px" }} color="neutral">
            {title}:
        </Typography>
        <Skeleton variant="text" width="60%" height={20} />
    </Box>
);

const SkeletonAssetInformation = ({ showImei, t }: { showImei?: boolean, t: (key: string) => string }) => (
    <Box>
        <SkeletonKeyValue title={t('Serial No.')} />
        {showImei && <SkeletonKeyValue title={t('IMEI')} />}
        <SkeletonKeyValue title={t('Product code')} />
        <SkeletonKeyValue title={t('Manufacturer')} />
    </Box>
);

const SkeletonNormalView = ({ onOpenAsset, t }: { onOpenAsset?: boolean, t: (key: string) => string }) => (
    <Box display="flex" flexDirection="column">
        <Box sx={{ paddingBottom: "12px" }} alignSelf="center">
            <Skeleton variant="rectangular" width={200} height={200} sx={{ borderRadius: "8px" }} />
        </Box>
        <Skeleton variant="text" height={32} sx={{ alignSelf: "center", width: "80%" }} />
        <Box sx={{ padding: "12px 8px" }}>
            <SkeletonAssetInformation showImei t={t} />
        </Box>
        <Skeleton variant="rectangular" height={32} sx={{ borderRadius: "16px" }} />
        {onOpenAsset && (
            <Skeleton variant="rectangular" height={36} sx={{ mt: "13px", borderRadius: "4px" }} />
        )}
    </Box>
);

const SkeletonCompactView = ({ t }: { t: (key: string) => string }) => (
    <Box display="flex" flexDirection="column">
        <Box display="flex" alignItems="center">
            <Box alignSelf="center">
                <Skeleton variant="rectangular" width={85} height={85} sx={{ borderRadius: "8px" }} />
            </Box>
            <Box flexGrow={1} sx={{ pl: 3 }}>
                <Skeleton variant="text" height={28} width="70%" />
                <SkeletonAssetInformation showImei t={t} />
            </Box>
        </Box>
    </Box>
);

interface Props {
    /** When set, the nameplate is sourced from the asset's current location (full info incl. status / IoT). */
    assetLocationId?: string,
    /** Fallback when no location exists yet (e.g. install-from-register flow): renders just the asset. */
    assetId?: string,
    compact?: boolean,
    onOpenAsset?: () => void,
    /** When set, renders an outlined "Remove asset" button below "Open asset". The caller decides
     *  whether removal is available and what the click does (e.g. opening the remove-event modal). */
    onRemoveAsset?: () => void,
    /** When set, renders an outlined "On-site Intervention" button in the same slot as
     *  "Remove asset". The caller decides availability (mutually exclusive with onRemoveAsset). */
    onOnsiteIntervention?: () => void,
    /** Disables click-to-navigate on the title/image (e.g. when rendered inside a modal). */
    disableNavigation?: boolean,
    /** Hides the node-image upload/remove button on the 200px image (e.g. inside tooltips). */
    hideImageActions?: boolean,
    /** Adds a single-line "Location" row with the installed base path (location mode only). */
    showLocation?: boolean,
}

const AssetNameplate = ({assetLocationId, assetId, compact, onOpenAsset, onRemoveAsset, onOnsiteIntervention, disableNavigation, hideImageActions, showLocation}: Props) => {

    const {t} = useTranslation();
    const navigate = useNavigate();

    const useLocationMode = !!assetLocationId;

    // Title/image navigate to the asset detail view, unless disabled (e.g. inside a modal).
    // Only available in location mode — asset-only mode has no detail route.
    const handleNavigate = (!disableNavigation && assetLocationId)
        ? () => navigate(`/assets/${assetLocationId}/details`)
        : undefined;
    const clickableSx = handleNavigate ? { cursor: "pointer" } : undefined;

    // Location-mode query: status + IoT + node title.
    const basicInfoQuery = useQuery(AssetNameplateBasicInfo, {
        variables: {
            id: assetLocationId!,
            imageCrop: "fit",
            imageWidth: 200,
            imageHeight: 200,
        },
        skip: !useLocationMode,
    });

    const iotQuery = useQuery(AssetIotConnection, {
        variables: { id: assetLocationId! },
        skip: !useLocationMode,
    });

    // Only when the location row is requested — keeps the other nameplates' queries unchanged.
    const pathQuery = useQuery(AssetBreadcrumbPath, {
        variables: { id: assetLocationId! },
        skip: !useLocationMode || !showLocation,
    });

    // Asset-only mode: no location yet, fetch asset details directly.
    const assetOnlyQuery = useQuery(AssetDetail, {
        variables: {
            filter: { id: { equals: assetId } },
            imageCrop: "fit",
            imageWidth: 200,
            imageHeight: 200,
        },
        skip: useLocationMode || !assetId,
    });

    const assetLocationData = useLocationMode ? _.get(basicInfoQuery, 'data.assetLocation') : undefined;
    const assetData = useLocationMode
        ? _.get(assetLocationData, 'asset')
        : _.get(assetOnlyQuery, 'data.asset');
    const iotData = _.get(iotQuery, 'data.assetLocation.iotConnection');
    const isLoading = useLocationMode ? basicInfoQuery.loading : assetOnlyQuery.loading;
    const queryError = useLocationMode ? basicInfoQuery.error : assetOnlyQuery.error;
    const productName = _.get(assetData, 'product.name');
    // The node title falls back to the product name (asset-only mode has no node at all).
    const headlineTitle = useLocationMode
        ? _.get(assetLocationData, 'installedBaseNode.title') || productName
        : productName;

    const connectionStatus = _.get(iotData, 'connectionStatus');
    const imei = _.get(iotData, 'gateway.imei');
    // Placeholder image is branded by the owning client (not the logged-in user).
    const ownerIndustryType = _.get(assetLocationData, 'owner.industryType');
    // A user-set node image replaces the catalog product image; the product image
    // stays as fallback in case the node thumbnail fails to load. Asset-only mode
    // has no node and therefore no node image.
    const installedBaseNode = _.get(assetLocationData, 'installedBaseNode');
    const nodeImage = getNodeImageThumbnail(installedBaseNode);
    const productImage = _.get(assetData, 'product.image.thumbnailLink');
    const nodeImageTarget = buildNodeImageTarget(installedBaseNode);
    const locationPathNodes = getPathNodesAbove(_.get(pathQuery, 'data.assetLocation.installedBaseNode'));

    // Phase 1: Show full skeleton while basic info loads
    if (isLoading) {
        return compact ? (
            <SkeletonCompactView t={t} />
        ) : (
            <SkeletonNormalView onOpenAsset={!!onOpenAsset} t={t} />
        );
    }

    // Handle errors
    if (queryError) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" p={2}>
                <Typography level="body-sm" color="danger">
                    {t('Failed to load asset information')}
                </Typography>
            </Box>
        );
    }

    const AssetInformation = () => <Box>
        <KeyValueInformation title={t('Serial No.')} value={_.get(assetData, 'serialNumber')} />

        {/* IMEI field with progressive loading — only relevant when we have a location */}
        {useLocationMode && (iotQuery.loading || imei) && (
            iotQuery.loading ? (
                <SkeletonKeyValue title={t('IMEI')} />
            ) : (
                <KeyValueInformation title={t('IMEI')} value={imei} />
            )
        )}

        {/* Only shown when the node was renamed away from the product name — disambiguates the headline. */}
        {productName && headlineTitle !== productName && (
            <KeyValueInformation
                title={t('Product')}
                value={
                    // Single line, ellipsized; the full name is on hover.
                    <Tooltip title={productName} enterDelay={700} placement="top-start">
                        <Typography
                            level="body-sm"
                            fontWeight="lg"
                            noWrap
                            sx={{ width: "60%", minWidth: 0, color: "#333" }}
                        >
                            {productName}
                        </Typography>
                    </Tooltip>
                }
            />
        )}
        <KeyValueInformation title={t('Product code')} value={_.get(assetData, 'product.productCode')} />
        <KeyValueInformation
            title={t('Manufacturer')}
            value={_.get(assetData, 'product.manufacturer.name') ?? _.get(assetData, 'manufacturer.name')}
        />

        {useLocationMode && showLocation && (
            pathQuery.loading ? (
                <SkeletonKeyValue title={t('Location')} />
            ) : locationPathNodes.length > 0 && (
                <KeyValueInformation
                    title={t('Location')}
                    value={
                        <Box sx={{ width: "60%", minWidth: 0 }}>
                            <InstalledBasePathLine pathNodes={locationPathNodes} />
                        </Box>
                    }
                />
            )
        )}
    </Box>

    if (compact) {
        return (
            <Box display={"flex"} flexDirection={"column"}>
                <Box display={"flex"} alignItems={"center"}>
                    <Box alignSelf="center" onClick={handleNavigate} sx={clickableSx}>
                        <AssetProductImage
                            src={nodeImage ?? productImage}
                            fallbackSrc={nodeImage ? productImage : undefined}
                            industryType={ownerIndustryType}
                            style={{height:  85, width: 85}}
                        />
                    </Box>

                    <Box flexGrow={1} sx={{pl: 3}}>
                        <Typography
                            level={"titleMedium"}
                            fontWeight={"bold"}
                            onClick={handleNavigate}
                            sx={clickableSx}
                        >
                            {headlineTitle}
                        </Typography>
                        <AssetInformation />
                    </Box>
                </Box>
            </Box>
        )
    }

    return (
        <Box display={"flex"} flexDirection={"column"}>
            {/* The action button is a sibling of the clickable box (whose click
                navigates), inside the hover-target wrapper it is positioned in. */}
            <Box className={NODE_IMAGE_HOVER_TARGET_CLASS} sx={{position: "relative", marginBottom: "12px"}} alignSelf="center">
                <Box sx={clickableSx} onClick={handleNavigate}>
                    <AssetProductImage
                        src={nodeImage ?? productImage}
                        fallbackSrc={nodeImage ? productImage : undefined}
                        industryType={ownerIndustryType}
                    />
                </Box>
                {useLocationMode && !hideImageActions && (
                    <NodeImageActionButton target={nodeImageTarget}/>
                )}
            </Box>

            <Typography
                level="titleLargeBold"
                fontWeight="bold"
                textAlign="center"
                onClick={handleNavigate}
                sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    wordBreak: "break-word",
                    ...clickableSx,
                }}
            >
                {headlineTitle}
            </Typography>
            <Box sx={{padding: "12px 8px"}}>
                <AssetInformation />
            </Box>
            {/* Status Chip — IoT statuses only; assets without an IoT connection get no chip */}
            {useLocationMode && (iotQuery.loading) ? (
                <Skeleton variant="rectangular" height={24} sx={{ borderRadius: "16px", width: "100%" }} />
            ) : (
                useLocationMode && connectionStatus && (() => {
                    const { bg, text } = getStatusChipColors(connectionStatus);
                    return (
                        <Chip
                            sx={{
                                maxWidth: "100%",
                                textAlign: "center",
                                width: "100%",
                                backgroundColor: bg,
                                color: text,
                            }}
                        >
                            {getStatusChipLabel(t, connectionStatus)}
                        </Chip>
                    );
                })()
            )}

            {onOpenAsset &&
                <Button
                    variant={"contained"}
                    color={"secondary"}
                    startIcon={<OpenInNewOutlined />}
                    sx={{mt: "13px"}}
                    onClick={onOpenAsset}
                >
                    {t("Open asset")}
                </Button>
            }

            {onRemoveAsset &&
                <Button
                    variant={"outlined"}
                    color={"secondary"}
                    startIcon={<RemoveRounded />}
                    sx={{mt: "8px"}}
                    onClick={onRemoveAsset}
                >
                    {t("Remove asset")}
                </Button>
            }

            {onOnsiteIntervention &&
                <Button
                    variant={"outlined"}
                    color={"secondary"}
                    startIcon={<EngineeringRounded />}
                    sx={{mt: "8px"}}
                    onClick={onOnsiteIntervention}
                >
                    {t("On-site Intervention")}
                </Button>
            }
        </Box>
    );
}

export default AssetNameplate;
